import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  useTheme, 
  useMediaQuery,
  Box, 
  Typography,
  Stack, 
  Card, 
  CardContent, 
  CardHeader,
  FormControl, 
  Select, 
  MenuItem,
  Button, 
  Chip, 
  Divider,
  CircularProgress, 
  alpha,
} from '@mui/material';
import { AddCircleOutline, DeleteForever, Search } from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import Header from 'components/Header';
import ConfirmDialog from 'components/ConfirmDialog';
import FilterRow from './FilterRow';
import PreviewCard from './PreviewCard';
import HistoryCard from './HistoryCard';
import { NO_VALUE_OPS, newFilter } from './constants';


const PurgeData = () => {
  useAuthCheck();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollRef = useRef(null);

  const [allCollections, setAllCollections] = useState([]);
  const [collection, setCollection]         = useState('');
  const [schema, setSchema]                 = useState([]);
  const [schemaLoading, setSchemaLoading]   = useState(false);
  const [filters, setFilters]               = useState([newFilter()]);

  const [previewData, setPreviewData]       = useState(null);
  const [previewPage, setPreviewPage]       = useState(1);
  const [previewing, setPreviewing]         = useState(false);

  const [purging, setPurging]               = useState(false);
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [history, setHistory]               = useState([]);

  useEffect(() => {
    api.get('/purgedata/collections')
      .then(res => setAllCollections(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!collection) { setSchema([]); return; }
    setSchemaLoading(true);
    setFilters([newFilter()]);
    setPreviewData(null);
    api.get(`/purgedata/schema/${collection}`)
      .then(res => setSchema(res.data))
      .catch(() => setSchema([]))
      .finally(() => setSchemaLoading(false));
  }, [collection]);

  const updateFilter = (id, updated) => setFilters(f => f.map(r => r.id === id ? updated : r));
  const removeFilter = (id)          => setFilters(f => f.filter(r => r.id !== id));
  const addFilter    = ()            => setFilters(f => [...f, newFilter()]);

  const isFiltersReady = useMemo(() =>
    filters.every(f =>
      f.field && f.operator &&
      (NO_VALUE_OPS.has(f.operator) || f.value !== '') &&
      (f.operator !== 'between' || f.valueTo !== '')
    ),
  [filters]);

  const handlePreview = useCallback(async (page = 1) => {
    if (!collection || !isFiltersReady) return;
    try {
      setPreviewing(true);
      const res = await api.post('/purgedata/preview', { collection, filters, page, pageSize: 20 });
      setPreviewData(res.data);
      setPreviewPage(res.data.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }, [collection, filters, isFiltersReady]);

  const handleExecute = useCallback(async () => {
    try {
      setPurging(true);
      const res = await api.delete('/purgedata/execute', { data: { collection, filters } });
      setHistory(h => [{ ...res.data, timestamp: new Date() }, ...h]);
      setPreviewData(null);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  }, [collection, filters]);

  const confirmMessage = previewData?.total != null
    ? `Permanently delete ${previewData.total.toLocaleString()} record(s) from "${collection}" matching the configured filters?`
    : undefined;

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Purge Data" 
          subtitle="Database Housekeeping" 
        />
      </FlexBetween>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{ 
            gridColumn: "span 12", 
            overflowY: 'auto', 
            ...(!isMobile && { pr: 0.5 }) 
          }}
        >      
          {/* ── Configure Card ── */}
          <Card 
            sx={{ borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'divider', 
              flexShrink: 0 
            }}
          >
            <CardHeader
              title={<Typography variant="subtitle2" fontWeight={600}>Configure</Typography>}
              sx={{
                py: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            />
            <CardContent sx={{ pt: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack spacing={1}>
                {/* Collection selector */}
                <Box 
                  display="flex" 
                  flexWrap="wrap"
                  alignItems="center" 
                  gap={2} 
                >
                  <FormControl 
                    size="small" 
                    fullWidth={isMobile} 
                    sx={{ minWidth: isMobile ? undefined : 250 }}
                  >
                    <Select
                      value={collection}
                      onChange={e => setCollection(e.target.value)}
                      displayEmpty
                      renderValue={v => v || <Typography color="text.disabled" variant="body2">Select collection…</Typography>}
                    >
                      {allCollections?.map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {schemaLoading && <CircularProgress size={18} />}
                  {schema.length > 0 && !schemaLoading && (
                    <Typography variant="caption" color="text.secondary">
                      {schema.length} fields detected
                    </Typography>
                  )}
                </Box>

                {/* Filter rows */}
                {schema.length > 0 && (
                  <>
                    <Divider />
                    <Stack spacing={isMobile ? 1.5 : 0.5}>
                      {filters.map((f, idx) => (
                        <FilterRow
                          key={f.id}
                          filter={f}
                          schema={schema}
                          onChange={updated => updateFilter(f.id, updated)}
                          onRemove={() => removeFilter(f.id)}
                          index={idx}
                          isMobile={isMobile}
                        />
                      ))}
                    </Stack>

                    {/* Action buttons */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        size="small"
                        fullWidth={isMobile}
                        variant="outlined"                        
                        startIcon={<AddCircleOutline />}
                        onClick={addFilter}
                      >
                        Add Filter
                      </Button>

                      <Button
                        size="small"
                        fullWidth={isMobile}
                        variant="outlined"                        
                        color="secondary"
                        startIcon={previewing ? <CircularProgress size={14} /> : <Search />}
                        disabled={!isFiltersReady || previewing}
                        onClick={() => handlePreview(1)}
                      >
                        Preview
                      </Button>

                      <Button
                        size="small"
                        fullWidth={isMobile}
                        variant="contained"
                        color="error"
                        startIcon={purging ? <CircularProgress size={14} color="inherit" /> : <DeleteForever />}
                        onClick={() => setConfirmOpen(true)}
                        disabled={!previewData || previewData.total === 0 || purging}
                      >
                        Execute
                        {previewData?.total > 0 && (
                          <Chip
                            label={previewData.total.toLocaleString()}
                            size="small"
                            sx={{ ml: 1, height: 18, bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontSize: 11 }}
                          />
                        )}
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          <PreviewCard
            previewData={previewData}
            previewPage={previewPage}
            onPageChange={handlePreview}
          />

          <HistoryCard
            history={history}
            isMobile={isMobile}
          />

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleExecute}
        title="Confirm Purge"
        message={confirmMessage}
        confirmText="Delete Permanently"
        confirmColor="error"
      />
    </Box>
  );
};

export default PurgeData;
